import React, { useState, KeyboardEvent } from 'react';
import cx from 'classnames';

import './tooltip.css';

/**
 * Displays a box that hovers near a trigger to provide contextual information.
 * @param props {@link TooltipProps}
 */
export function Tooltip<RenderMessageProps = void>(
    props: TooltipProps<RenderMessageProps>,
): JSX.Element {
    const {
        addClasses,
        background = 'dark',
        children,
        disabled = false,
        maxWidth = 200,
        message,
        messageClasses,
        minWidth = 200,
        padding = 'sm',
        position = 'top',
        RenderMessage,
        renderMessageProps,
        zIndex = 30,
    } = props;
    let closeOnMouseLeave = props?.closeOnMouseLeave ?? true;
    let trigger = props?.trigger ?? 'mouseOver';

    if (RenderMessage) {
        closeOnMouseLeave = false;
        trigger = 'click';
    }

    const [displayTooltip, setDisplayTooltip] = useState(false);
    const triggerClasses = cx(
        'trigger inline-block text-left p-0',
        addClasses,
        background,
        {
            'cursor-default': trigger === 'mouseOver',
            'hover:cursor-pointer': trigger === 'click',
        },
    );

    return (
        <span
            className="tooltip relative"
            onMouseLeave={(): void => {
                if (closeOnMouseLeave) {
                    setDisplayTooltip(false);
                }
            }}
        >
            {displayTooltip && (
                <div
                    className={cx(
                        `tooltip-bubble absolute tooltip-${position}`,
                        background,
                    )}
                    style={{
                        minWidth,
                        maxWidth,
                        zIndex,
                    }}
                >
                    <div
                        className={cx(
                            'tooltip-message rounded-md text-sm leading-snug break-words',
                            background,
                            messageClasses,
                            {
                                'p-3': padding === 'sm',
                            },
                        )}
                    >
                        {message && message}
                        {RenderMessage && (
                            <RenderMessage
                                close={(): void => {
                                    setDisplayTooltip(false);
                                }}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...renderMessageProps}
                            />
                        )}
                    </div>
                </div>
            )}
            <div
                className={triggerClasses}
                role="button"
                tabIndex={0}
                onMouseOver={(): void => {
                    if (trigger === 'mouseOver' && !disabled) {
                        setDisplayTooltip(true);
                    }
                }}
                onFocus={(): void => {
                    if (!disabled) {
                        setDisplayTooltip(true);
                    }
                }}
                onClick={(): void => {
                    if (trigger === 'click' && !disabled) {
                        setDisplayTooltip(true);
                    }
                }}
                onKeyPress={(e: KeyboardEvent): void => {
                    if (e.key === 'Enter' && trigger === 'click' && !disabled) {
                        setDisplayTooltip(true);
                    }
                }}
            >
                {children && <>{children}</>}
            </div>
        </span>
    );
}
